import { kbApi } from '@carrent/api'
import { network, storage } from '@carrent/platform'

/**
 * Закреплённые инструкции на устройстве.
 *
 * ── Зачем ─────────────────────────────────────────────────────────
 *
 * Закрепляют именно то, что нужно в худший момент: порядок действий
 * при ДТП, правила фотофиксации. Худший момент — это обычно подземный
 * гараж или трасса без сети, где обычный запрос вернёт ошибку.
 * Поэтому закреплённые статьи держим на устройстве целиком.
 *
 * ── Что не кэшируем ───────────────────────────────────────────────
 *
 * Только закреплённые и только текст. Вся база знаний не влезет
 * в разумный объём, а картинки-вложения быстро съедят место, которого
 * на телефоне водителя и так мало. Картинка в офлайне не покажется —
 * это осознанный размен: текст инструкции важнее иллюстрации.
 *
 * ── Когда обновляем ───────────────────────────────────────────────
 *
 * При появлении сети и при заходе в раздел. Кэш не протухает по
 * таймеру: устаревшая инструкция полезнее её отсутствия, а дата
 * обновления показывается рядом, чтобы водитель понимал, что читает.
 */

const KEY = 'kb:pinned'

/** Прочитать сохранённое. Возвращает { articles, savedAt } либо null. */
export function readCache() {
  const raw = storage.get(KEY)
  if (!raw || !Array.isArray(raw.articles)) return null
  return raw
}

/**
 * Скачать закреплённые статьи и положить на устройство.
 *
 * Молча ничего не делает без сети: обновление кэша — фоновая
 * задача, она не должна показывать ошибок поверх экрана.
 */
export async function refreshCache() {
  if (!network.isOnline()) return readCache()

  try {
    const spacesR = await kbApi.spaces()
    const spaces = spacesR.data?.data || []

    const trees = await Promise.all(
      spaces.map((s) => kbApi.tree(s.key)
        .then((r) => ({ space: s, pages: r.data?.data?.pages || [] }))
        .catch(() => ({ space: s, pages: [] }))),
    )

    const pinned = trees.flatMap(({ space, pages }) =>
      pages.filter((p) => p.pinned && p.status === 'published')
        .map((p) => ({ ...p, space_key: space.key, space_name: space.name })))

    const articles = await Promise.all(
      pinned.map((p) => kbApi.page(p.id)
        .then((r) => ({
          id: p.id,
          title: p.title,
          space_key: p.space_key,
          space_name: p.space_name,
          body_md: r.data?.data?.body_md || '',
        }))
        .catch(() => null)),
    )

    const clean = articles.filter(Boolean)
    const payload = { articles: clean, savedAt: new Date().toISOString() }
    storage.set(KEY, payload)
    return payload
  } catch {
    // Сеть моргнула посреди обновления — оставляем прежний кэш.
    return readCache()
  }
}

/** Статья из кэша по идентификатору. */
export function cachedArticle(pageId) {
  const cache = readCache()
  if (!cache) return null
  return cache.articles.find((a) => a.id === Number(pageId)) || null
}

/** Подписка на появление сети: обновляем кэш, когда связь вернулась. */
export function watchNetwork() {
  return network.subscribe((online) => {
    if (online) refreshCache()
  })
}

export const CACHE_KEY = KEY
